// NCALayer Service для работы с Kalkan цифровыми подписями
// Адаптированный для проекта chickenPF

export class CustomWebSocket extends WebSocket {
    constructor(url: string, protocols?: string | string[]) {
        url = url.replace(/(?<=wss:\/\/).*?(?=127\.0\.0\.1)/, '');
        super(url, protocols);

        // Обработка ошибок самоподписанного сертификата
        this.addEventListener('error', (event) => {
            console.warn('WebSocket connection error (likely self-signed certificate):', event);
        });
    }
}

type NcaResponse<T = unknown> = {
    code: '200' | '500';
    message: string;
    responseObject: T;
    requestId?: string;
};

export interface SignatureResult {
    signature: string;
    certificate: string;
    signerInfo: {
        commonName: string;
        organization: string;
        country: string;
        serialNumber: string;
        validFrom: string;
        validTo: string;
    };
}

export interface VerificationResult {
    valid: boolean;
    message: string;
    certificateInfo?: any;
}

export class Pki {
    private _websocket: WebSocket | null = null;
    private _callback: (result: string) => void = () => { };
    private _onError: (error: { message: string }) => void = () => { };
    private _certificateCache: { [key: string]: any } = {}; // Кеш для информации о сертификатах
    private _translations: Record<string, string> = {
        pkiToken: 'PKCS12',
        identification: 'Идентификация',
        KAZTOKEN: 'KAZTOKEN',
        X509CERT: 'X509CERT',
        initErr: 'Ошибка инициализации',
        initMoreErr: 'Подключение ещё не завершено',
        updateErr: 'Переинициализация требуется',
        canceled: 'Действие отменено',
        connectionErr: 'Ошибка подключения к NCA Layer',
    };
    private _token: string = 'PKCS12';

    private _pendingResolvers: Array<(data: unknown) => void> = [];
    private _pendingRejectors: Array<(error: { message: string }) => void> = [];

    public sendAndWait<T = unknown>(message: object): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            this._pendingResolvers.push((data) => resolve(data as T));
            this._pendingRejectors.push(reject);
            this._websocket?.send(JSON.stringify(message));
        });
    }

    private tokens: Record<string, string> = {
        PKCS12: '',
        KZIDCARD: '',
        AKKZIDCardStore: '',
        KAZTOKEN: '',
        AKKaztokenStore: '',
        ETOKEN72K: 'eToken 72k',
        AKEToken72KStore: 'eToken 72k',
        JACARTA: 'JaCarta',
        X509CERT: '',
        AKEY: 'aKey',
        ETOKEN5110: 'eToken 5110',
    };

    public start() {
        try {
            this._websocket = new CustomWebSocket('wss://127.0.0.1:13579/');
            this._websocket.onmessage = this._onMessage;
            this._websocket.onclose = this._onClose;
            this._websocket.onerror = (error) => {
                console.warn('NCA Layer WebSocket error (self-signed certificate is normal):', error);
            };
        } catch (error) {
            console.error('Failed to start NCA Layer WebSocket:', error);
        }
    }

    public config(params: {
        onError?: (error: { message: string }) => void;
        token?: string;
        translations?: {
            pkiToken?: string;
            identification?: string;
            KAZTOKEN?: string;
            X509CERT?: string;
            initErr?: string;
            initMoreErr?: string;
            updateErr?: string;
            canceled?: string;
            connectionErr?: string;
        };
    }) {
        if (params.onError) this._onError = params.onError;
        if (params.token) this._token = params.token;
        if (params.translations) this._translations = { ...this._translations, ...params.translations };

        if (this._translations) {
            this.tokens = {
                ...this.tokens,
                PKCS12: this._translations.pkiToken ?? 'PKCS12',
                KZIDCARD: this._translations.identification ?? 'KZIDCARD',
                AKKZIDCardStore: this._translations.identification ?? 'AKKZIDCardStore',
                KAZTOKEN: this._translations.KAZTOKEN ?? 'KAZTOKEN',
                AKKaztokenStore: this._translations.KAZTOKEN ?? 'AKKaztokenStore',
                X509CERT: this._translations.X509CERT ?? 'X509CERT',
            };
        }
    }

    public async signBase64(base64: string, callback: (signedCms: string) => void, flag: boolean = true) {
        if (!this._checkState()) return;

        try {
            const cmsWithTimestamp = await this.sendAndWait<string>({
                module: 'kz.gov.pki.knca.commonUtils',
                method: 'createCMSSignatureFromBase64',
                args: [this._token, 'SIGNATURE', base64, flag],
            });

            callback(cmsWithTimestamp);
        } catch (err: unknown) {
            let message = 'Ошибка при подписании';

            if (err instanceof Error) {
                message = err.message;
            } else if (typeof err === 'string') {
                message = err;
            } else if (typeof err === 'object' && err !== null && 'message' in err) {
                message = String((err as { message: unknown }).message);
            }

            this._onError({ message });
        }
    }

    // Получение списка доступных сертификатов
    public async getCertificates(): Promise<any[]> {
        if (!this._checkState()) return [];

        try {
            const activeTokens = await this.sendAndWait<string[]>({
                module: 'kz.gov.pki.knca.commonUtils',
                method: 'getActiveTokens',
                args: []
            });

            const certificates = [];

            // Добавляем PKCS12 по умолчанию
            certificates.push({
                path: 'PKCS12',
                alias: this.tokens.PKCS12 || 'PKCS12',
                subject: 'PKCS12 Certificate Store',
                validFrom: '',
                validTo: ''
            });

            // Добавляем найденные токены
            if (Array.isArray(activeTokens)) {
                for (const token of activeTokens) {
                    certificates.push({
                        path: token,
                        alias: this.tokens[token] || token,
                        subject: `${token} Certificate Store`,
                        validFrom: '',
                        validTo: ''
                    });
                }
            }

            return certificates;
        } catch (error) {
            console.error('Ошибка получения сертификатов:', error);
            return [{
                path: 'PKCS12',
                alias: this.tokens.PKCS12 || 'PKCS12',
                subject: 'PKCS12 Certificate Store',
                validFrom: '',
                validTo: ''
            }];
        }
    }

    // Проверка доступности NCALayer
    public async isAvailable(): Promise<boolean> {
        return new Promise((resolve) => {
            const timeout = 5000; // 5 секунд на попытку подключения
            let resolved = false;
            let timeoutId: ReturnType<typeof setTimeout>;

            const cleanup = () => {
                if (timeoutId) clearTimeout(timeoutId);
            };

            const resolveOnce = (value: boolean) => {
                if (!resolved) {
                    resolved = true;
                    cleanup();
                    resolve(value);
                }
            };

            try {
                // Закрываем старое соединение если оно не в рабочем состоянии
                if (this._websocket && this._websocket.readyState !== WebSocket.OPEN) {
                    try {
                        this._websocket.close();
                    } catch (e) {
                        // Игнорируем ошибку закрытия
                    }
                    this._websocket = null;
                }

                // Если уже подключены - проверяем реально ли работает соединение
                if (this._websocket?.readyState === WebSocket.OPEN) {
                    console.log('✅ NCA Layer WebSocket already connected');
                    resolveOnce(true);
                    return;
                }

                // Создаем новое подключение
                console.log('🔄 Attempting to connect to NCA Layer...');
                const ws = new CustomWebSocket('wss://127.0.0.1:13579/');

                // Таймаут на подключение
                timeoutId = setTimeout(() => {
                    console.warn('⏱️ NCA Layer connection timeout - NCALayer не запущен');
                    try {
                        ws.close();
                    } catch (e) {
                        // Игнорируем
                    }
                    resolveOnce(false);
                }, timeout);

                ws.onopen = () => {
                    console.log('✅ NCA Layer WebSocket connected successfully');
                    this._websocket = ws;
                    // Устанавливаем обработчики на новый сокет
                    ws.onmessage = this._onMessage;
                    ws.onclose = this._onClose;
                    resolveOnce(true);
                };

                ws.onerror = (error) => {
                    console.error('❌ NCA Layer WebSocket connection error:', error);
                    // Если получили ошибку - NCALayer не доступен
                    resolveOnce(false);
                };

                ws.onclose = (event) => {
                    // Если соединение закрылось до того как мы его установили
                    if (!resolved) {
                        console.warn('❌ NCA Layer WebSocket closed before connection established');
                        resolveOnce(false);
                    }
                };

            } catch (error) {
                console.error('❌ Error checking NCA Layer availability:', error);
                resolveOnce(false);
            }
        });
    }

    // Подписание данных с возвратом структурированного результата
    public async signData(data: string, certificatePath: string, password?: string): Promise<SignatureResult> {
        if (!this._checkState()) {
            throw new Error('NCALayer не подключен');
        }

        try {
            // Кодируем данные в Base64
            const encodedData = btoa(unescape(encodeURIComponent(data)));

            console.log('🔄 Starting signing process with sequential NCA Layer calls...');

            // ВАЖНО: Делаем запросы последовательно, а не параллельно!
            // Параллельные запросы могут зависать из-за проблем с очередью резолверов
            
            // Сначала получаем информацию о сертификате
            console.log('📋 Step 1: Getting certificate info...');
            const certInfo = await this.getCertificateInfo(certificatePath);
            console.log('✅ Step 1 complete: Certificate info received');

            // Затем подписываем данные
            console.log('🔐 Step 2: Creating signature...');
            const signatureData = await this.sendAndWait<string>({
                module: 'kz.gov.pki.knca.commonUtils',
                method: 'createCMSSignatureFromBase64',
                args: [certificatePath, 'SIGNATURE', encodedData, true]
            });
            console.log('✅ Step 2 complete: Signature created');

            console.log('🔐 NCA Layer signature response:', {
                signatureLength: signatureData?.length || 0,
                signaturePreview: signatureData?.substring(0, 100) + '...',
                certificatePath
            });

            console.log('📜 Certificate info from NCA Layer:', certInfo);
            console.log('📜 Certificate info keys:', certInfo ? Object.keys(certInfo) : 'null');
            console.log('📜 Certificate info values:', {
                commonName: certInfo?.commonName,
                organization: certInfo?.organization,
                serialNumber: certInfo?.serialNumber,
                validFrom: certInfo?.validFrom,
                validTo: certInfo?.validTo,
                certificate: certInfo?.certificate ? 'Present (length: ' + certInfo.certificate.length + ')' : 'Missing'
            });

            const result = {
                signature: signatureData,
                certificate: certInfo.certificate || '',
                signerInfo: {
                    commonName: certInfo.commonName || '',
                    organization: certInfo.organization || '',
                    country: certInfo.country || 'KZ',
                    serialNumber: certInfo.serialNumber || '',
                    validFrom: certInfo.validFrom || '',
                    validTo: certInfo.validTo || ''
                }
            };

            console.log('✅ Final SignatureResult object (optimized):', {
                signatureLength: result.signature?.length || 0,
                certificateLength: result.certificate?.length || 0,
                signerInfo: result.signerInfo
            });
            console.log('✅ SignerInfo details:', {
                commonName: result.signerInfo.commonName,
                organization: result.signerInfo.organization,
                serialNumber: result.signerInfo.serialNumber,
                validFrom: result.signerInfo.validFrom,
                validTo: result.signerInfo.validTo
            });

            return result;
        } catch (error) {
            console.error('Ошибка подписания:', error);
            throw error;
        }
    }

    // Получение информации о сертификате с кешированием
    public async getCertificateInfo(certificatePath: string): Promise<any> {
        if (!this._checkState()) return {};

        // Проверяем кеш
        if (this._certificateCache[certificatePath]) {
            console.log('📋 Используем кешированную информацию о сертификате для:', certificatePath);
            return this._certificateCache[certificatePath];
        }

        try {
            console.log('🔍 Запрашиваем информацию о сертификате для:', certificatePath);

            const certInfo = await this.sendAndWait<any>({
                module: 'kz.gov.pki.knca.commonUtils',
                method: 'getKeyInfo',
                args: [certificatePath]
            });

            console.log('📋 Полученная информация о сертификате:', {
                fullResponse: certInfo,
                keys: certInfo ? Object.keys(certInfo) : 'null',
                commonName: certInfo?.commonName,
                organization: certInfo?.organization,
                certificate: certInfo?.certificate ? 'Present (length: ' + certInfo.certificate.length + ')' : 'Missing'
            });

            // Кешируем результат
            const result = certInfo || {};
            this._certificateCache[certificatePath] = result;

            return result;
        } catch (error) {
            console.error('❌ Ошибка получения информации о сертификате:', error);
            return {};
        }
    }

    // Очистка кеша сертификатов
    public clearCertificateCache(): void {
        this._certificateCache = {};
        console.log('🗑️ Кеш сертификатов очищен');
    }

    // Проверка подписи
    public async verifySignature(signature: string, data?: string): Promise<VerificationResult> {
        if (!this._checkState()) {
            return { valid: false, message: 'NCALayer не подключен' };
        }

        try {
            const verifyParams = data ? [signature, data] : [signature];

            const result = await this.sendAndWait<any>({
                module: 'kz.gov.pki.knca.commonUtils',
                method: 'verifyCMSSignature',
                args: verifyParams
            });

            return {
                valid: result?.valid === true,
                message: result?.message || (result?.valid ? 'Подпись действительна' : 'Подпись недействительна'),
                certificateInfo: result?.certificateInfo
            };
        } catch (error) {
            console.error('Ошибка проверки подписи:', error);
            return {
                valid: false,
                message: `Ошибка проверки: ${error instanceof Error ? error.message : String(error)}`
            };
        }
    }

    private _checkState(): boolean {
        if (!this._websocket) {
            console.error('❌ WebSocket not initialized - NCALayer не подключен');
            return false;
        }

        switch (this._websocket.readyState) {
            case WebSocket.OPEN: // 1
                return true;
            case WebSocket.CONNECTING: // 0
                console.warn('⏳ WebSocket still connecting...');
                return false;
            case WebSocket.CLOSING: // 2
                console.error('❌ WebSocket is closing');
                return false;
            case WebSocket.CLOSED: // 3
                console.error('❌ WebSocket closed - NCALayer не подключен');
                return false;
            default:
                console.error('❌ Unknown WebSocket state:', this._websocket.readyState);
                return false;
        }
    }

    private _onMessage = (event: MessageEvent) => {
        const result: NcaResponse = JSON.parse(event.data);

        console.log('📨 Raw NCA Layer response:', {
            code: result.code,
            message: result.message,
            responseObjectType: typeof result.responseObject,
            responseObjectLength: typeof result.responseObject === 'string' ? result.responseObject.length : 'N/A',
            requestId: result.requestId
        });

        if (result.code === '500') {
            console.error('❌ NCA Layer error response:', result);
            const reject = this._pendingRejectors.shift();
            reject?.({ message: result.message });
        } else if (result.code === '200') {
            console.log('✅ NCA Layer success response:', {
                responseObject: result.responseObject,
                message: result.message
            });
            const resolve = this._pendingResolvers.shift();
            resolve?.(result.responseObject);
        }

        if (!this._pendingResolvers.length && this._callback) {
            this._callback(result.responseObject as string);
        }
    };

    private _onClose = (event: CloseEvent | MessageEvent) => {
        if ('code' in event) {
            if (event.code !== 1006) {
                this._onError({
                    message: this._translations?.connectionErr || 'Connection error',
                });
            }
        } else {
            const result = JSON.parse(event.data);
            this._onError({ message: result.message || 'Unknown error' });
        }
    };
}

// Создаем единственный экземпляр для использования в проекте
export const ncaLayerService = new Pki();

// Настраиваем обработчик ошибок по умолчанию
ncaLayerService.config({
    onError: (error) => {
        console.error('NCALayer Error:', error.message);
    }
});

export default ncaLayerService;